import React, { useEffect, useState } from "react";
import { useVehiculesStore } from "@store/vehiculesStore";
import { useDataStore } from "@store/user_org";
import { ordresApi } from "@services/api/ordres";
import OrdreModal from "@components/modals/OrdreModal";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import type { Ordre } from "types/ordre";
import { vehiclesApi, type UnassignedMobilityRequest } from "@services/api/vehicles";

const USAGE_LABEL: Record<string, string> = {
  pret: "Prêt",
  location: "Location",
  velos: "Vélo",
};

function fmt(ts: number | null): string {
  if (!ts) return "?";
  const d = new Date(ts * 1000);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm} ${d.getHours()}h${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Sous le calendrier : les ordres dont le client a demandé un véhicule de prêt /
 * location / vélo et qui n'en ont pas — l'attribution automatique n'a rien trouvé, ou
 * la voiture attribuée a été retirée de la flotte. Sans cette liste, on ne le
 * découvrait qu'à l'arrivée du client.
 */
export default function UnassignedMobilityRequests() {
  const orgId = useDataStore((s) => s.orgData?.id);
  // Relu à chaque changement des réservations : attribuer une voiture sort l'ordre de la liste.
  const reservations = useVehiculesStore((s) => s.reservations);
  const userData = useDataStore((s) => s.userData);
  const fetchReservations = useVehiculesStore((s) => s.fetchReservations);
  const [items, setItems] = useState<UnassignedMobilityRequest[] | null>(null);
  const [selectedOrdre, setSelectedOrdre] = useState<Ordre | null>(null);

  const openOrdre = (ordreId: string) => {
    ordresApi
      .get(ordreId)
      .then(setSelectedOrdre)
      .catch(() => showNotification({ type: "error", message: "Ordre introuvable" }));
  };

  const closeOrdre = () => {
    setSelectedOrdre(null);
    // Un véhicule attribué depuis la modale : le planning et cette liste se recalent.
    if (orgId) fetchReservations(orgId);
  };

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    vehiclesApi
      .unassignedMobilityRequests(orgId)
      .then((rows) => { if (!cancelled) setItems(rows); })
      // En erreur on masque la liste plutôt que d'afficher « tout va bien ».
      .catch(() => { if (!cancelled) setItems(null); });
    return () => { cancelled = true; };
  }, [orgId, reservations]);

  if (!items) return null;

  return (
    <div className="flex flex-col" style={{ gap: 6 }}>
      <div className="flex flex-row items-center justify-between">
        <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: "#374151" }}>
          Demandes sans véhicule
        </span>
        <span
          style={{
            fontFamily: "Poppins",
            fontWeight: 600,
            fontSize: 11,
            color: items.length ? "#B91C1C" : "#6B7280",
            background: items.length ? "#FEE2E2" : "#F3F4F6",
            borderRadius: 10,
            padding: "1px 7px",
          }}
        >
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#9CA3AF" }}>
          Toutes les demandes ont un véhicule.
        </span>
      ) : (
        items.map((it) => (
          <button
            key={it.ordre_id}
            type="button"
            onClick={() => openOrdre(it.ordre_id)}
            className="flex flex-col text-left hover:bg-neutral-50 transition-colors"
            style={{
              border: "1px solid #FECACA",
              borderLeft: "3px solid #EF4444",
              borderRadius: 6,
              padding: "6px 8px",
              gap: 2,
              background: "#fff",
            }}
            title="Ouvrir l'ordre"
          >
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 600,
                fontSize: 11,
                color: "#111827",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {it.name || it.num || "Ordre"}
            </span>
            <span style={{ fontFamily: "Poppins", fontSize: 10, color: "#6B7280" }}>
              {fmt(it.reception_ts)} → {fmt(it.restitution_ts)}
            </span>
            <span style={{ fontFamily: "Poppins", fontSize: 10, color: "#6B7280" }}>
              {USAGE_LABEL[it.usage || ""] || it.mobility_option}
              {it.immatriculation ? ` · ${it.immatriculation}` : ""}
            </span>
            {it.reason === "vehicle_deleted" && (
              <span style={{ fontFamily: "Poppins", fontSize: 10, color: "#B91C1C" }}>
                Voiture attribuée retirée de la flotte
              </span>
            )}
          </button>
        ))
      )}
      {selectedOrdre && (
        <OrdreModal
          ordre={selectedOrdre}
          onClose={closeOrdre}
          onSave={(updated) => setSelectedOrdre(updated)}
          onDelete={isOwnerOrAdmin(userData) ? closeOrdre : undefined}
        />
      )}
    </div>
  );
}
